// @flow

import React, { useEffect, useRef, useState } from 'react';
import { t } from 'javascripts/utils/withTranslation';
import { MotionConfig } from 'framer-motion/dist/framer-motion';
import className from 'classnames';
import type { Data, Wording } from '../types';
import FormGroup from '../../../molecules/form-group/react/FormGroup';
import Label from '../../../atoms/label/react/Label';
import Input from '../../../atoms/input/react/Input';
import Button from '../../../atoms/button/Button';
import Diagram from './Diagram';

type Props = {
  data?: Data,
  wording?: Wording,
  display: 'diagram' | 'list',
  setDisplay: () => void,
};

const Calculator = ({
  data,
  wording,
  display,
  setDisplay,
}: Props): React$MixedElement => {
  const { lang } = document.documentElement || { lang: 'de' };
  const [value, setValue] = useState('');
  const [calculateWithvalue, setCalculateWithvalue] = useState<number | void>();
  const [partialCalcValue, setPartialCalcValue] = useState<number | void>(
    calculateWithvalue
  );

  const handleSubmit = () => {
    if (value !== '') {
      setCalculateWithvalue(value);
    }
  };

  useEffect(() => {
    if (calculateWithvalue && calculateWithvalue > 0) {
      const partial = data?.table?.find((item) => item.title.includes('Land'));
      const partVal = partial?.value || 42.5;
      const calc = (partVal / 100) * calculateWithvalue;
      setPartialCalcValue(calc);
    }
  }, [calculateWithvalue]);

  return (
    <div className="tax-calculator__calc-frame">
      <form
        className="search tax-calculator__calc-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="search__inner">
          <div className="form-group">
            <Label htmlFor="tax-calculator-calc-input">
              {wording?.input?.text ||
                t('Tragen Sie hier Ihre Einkommensteuer in Euro ein:')}
            </Label>
            <div className="search__wrap">
              <Input
                type="number"
                name="tax-calculator-calc-input"
                value={value}
                classNames="input"
                onChange={(e) => setValue(e.target.value)}
                placeholder={
                  wording?.input?.placeholder || t('Einkommenssteuer in Euro')
                }
              />
            </div>
          </div>
          <div className="search__submit tax-calculator__calc-button">
            <Button type="submit">{t('Berechnen')}</Button>
          </div>
        </div>
      </form>
      {calculateWithvalue && calculateWithvalue > 0 && (
        <>
          <figure
            className="tax-calculator__table"
            ref={(n) => n?.scrollIntoView({ behavior: 'smooth' })}
          >
            <figcaption className="label form-group__label">
              {wording?.result?.text || t('Von diesem Betrag erhalten:')}
            </figcaption>
            <ul className="tax-calculator__table-list">
              {data?.table?.map((item, i) => (
                <li
                  key={item.title}
                  className={className(
                    'tax-calculator__table-item',
                    `tax-calculator__table-item--${i}`,
                    item.title.includes('Land') &&
                      'tax-calculator__table-item--highlight'
                  )}
                >
                  <div className="tax-calculator__table-title">
                    {item.title}
                  </div>
                  <div className="tax-calculator__table-value">
                    {new Intl.NumberFormat(lang, {
                      style: 'currency',
                      currency: 'EUR',
                    }).format((item.value / 100) * calculateWithvalue)}
                  </div>
                </li>
              ))}
            </ul>
          </figure>
          <div className="tax-calculator__diagram-frame">
            <div className="tax-calculator__hint">
              <p>
                {wording?.result?.info ||
                  t(
                    'Klicken Sie auf die Elemente und erfahren Sie detailliert, was mit dem Landesanteil Ihrer Einkommensteuer geschieht.'
                  )}
              </p>
            </div>
            {data?.diagram && (
              <MotionConfig reducedMotion="user">
                <Diagram
                  data={data.diagram}
                  inputValue={partialCalcValue}
                  variant="calculator"
                  display={display}
                  setDisplay={setDisplay}
                  wording={wording}
                />
              </MotionConfig>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Calculator;
